// One-way, non-destructive upgrade from the original single-calculator app.
// The legacy keys stay in place so an older build would still find them.

import { INSTRUMENTS } from "../lib/invest";
import { DEFAULT_RECOVERY, STORAGE } from "./defaults";
import { readJSON, readString } from "./storage";

/** Values the old calculator was last used with, if they differ from its defaults. */
export function readLegacyCalculator() {
  const legacy = readJSON(STORAGE.legacyLoan, null);
  if (!legacy || typeof legacy !== "object") return null;
  const principal = Number(legacy.principal);
  const tenure = Number(legacy.tenure);
  const rate = Number(legacy.interestRate);
  if (!(principal > 0) || !(tenure > 0) || !(rate >= 0)) return null;
  const untouched = principal === 50000 && tenure === 3 && rate === 8.5;
  return {
    principal,
    tenureYears: tenure,
    rate,
    inflationRate: Number(legacy.inflationRate) || 0,
    untouched,
  };
}

function legacyAllocation() {
  const pct = readJSON(STORAGE.legacyPercentages, null);
  if (!pct || typeof pct !== "object") return null;
  const out = {};
  let total = 0;
  for (const inst of INSTRUMENTS) {
    const v = Math.max(0, Number(pct[inst.legacyKey]) || 0);
    out[inst.id] = v;
    total += v;
  }
  if (total <= 0) return null;
  for (const id of Object.keys(out)) out[id] = Math.round((out[id] / total) * 10000) / 100;
  return out;
}

/** Initial recovery-plan state, seeded from legacy values on first run. */
export function initialRecovery(saved) {
  if (saved) {
    return {
      ...DEFAULT_RECOVERY,
      ...saved,
      custom: { ...DEFAULT_RECOVERY.custom, ...(saved.custom || {}) },
      goal: { ...DEFAULT_RECOVERY.goal, ...(saved.goal || {}) },
      allocation: { ...DEFAULT_RECOVERY.allocation, ...(saved.allocation || {}) },
    };
  }
  const next = { ...DEFAULT_RECOVERY };
  if (!readString(STORAGE.migrated)) {
    const calc = readLegacyCalculator();
    if (calc && !calc.untouched) {
      next.custom = { principal: calc.principal, tenureYears: calc.tenureYears, rate: calc.rate };
    }
    const alloc = legacyAllocation();
    if (alloc) next.allocation = alloc;
  }
  return next;
}
